import { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeGrid, GridChildComponentProps } from 'react-window';
import { Design, ProductType, Shop } from '@/types';
import { useDimension } from '@/hooks/useDimension';
import { useScrollbarWidth } from '@/hooks/useScrollbarWidth';
import { getContext } from '@/redux/core/getters/getContext';
import { getFeatures } from '@/redux/core/getters/getFeatures';
import { fetchDesignAction } from '@/redux/designs/actions/fetchDesignAction';
import { toggleDesignFavoriteAction } from '@/redux/designs/actions/toggleDesignFavoriteAction';
import { updateDesignFavoriteAction } from '@/redux/designs/actions/updateDesignFavoriteAction';
import { getDesignFavorites } from '@/redux/designs/getters/getDesignFavorites';
import { getDesigns } from '@/redux/designs/getters/getDesigns';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getPrintAreaForNewConfiguration } from '@/redux/draft/getters/getPrintAreaForNewConfiguration';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { StoreDispatch } from '@/redux/types';
import { DesignItem } from './DesignItem';
import { usePrintabilityMap } from './design-list-utils';
import './FavoriteList.scss';

interface CellData extends Pick<FavoriteListProps, 'selectDesign' | 'toggleDesignFavorite'> {
  columnCount: number;
  designs: Design[];
  printabilityMap: Map<string, boolean>;
  imageHeight: number;
  favoritedDesignIds: string[];
  showDesignPrices?: boolean;
  context: Shop;
  productType: ProductType;
}

interface CellProps extends GridChildComponentProps {
  data: CellData;
}

const Cell: FunctionComponent<CellProps> = ({
  columnIndex,
  rowIndex,
  style,
  data: {
    columnCount,
    designs,
    printabilityMap,
    imageHeight,
    favoritedDesignIds,
    showDesignPrices,
    context,
    productType,
    selectDesign,
    toggleDesignFavorite,
  },
}) => {
  const index = columnCount * rowIndex + columnIndex;
  if (index >= favoritedDesignIds.length) {
    return null;
  }

  const design = designs[index];
  return (
    <div style={style}>
      {design ? (
        <DesignItem
          onClick={selectDesign}
          imageHeight={imageHeight}
          design={design}
          isPrintable={printabilityMap.get(design.id)}
          isFavorited={true}
          isSelected={false}
          showDesignPrices={showDesignPrices}
          toggleDesignFavorite={toggleDesignFavorite}
          context={context}
          isMarketplaceCategoryActive={true}
          productType={productType}
        />
      ) : (
        <div className="favorite-list__placeholder" />
      )}
    </div>
  );
};

interface FavoriteListProps {
  selectDesign: (design: Design) => void;
  toggleDesignFavorite: (design: Design, isFavorited: boolean) => void;
  isSmall: boolean;
}

export const FavoriteList: FunctionComponent<FavoriteListProps> = ({
  selectDesign,
  toggleDesignFavorite,
  isSmall,
}) => {
  const itemWidth = isSmall ? 140 : 180;
  const imageHeight = isSmall ? 80 : 120;
  const dispatch = useDispatch<StoreDispatch>();
  const unfetchedDesignIds = useRef<string[]>([]);
  const favoritedDesignIds = useSelector(getDesignFavorites);
  const designsMap = useSelector(getDesigns);
  const designs = useMemo(
    () => favoritedDesignIds.map((designId) => designsMap[designId]).filter(Boolean),
    [favoritedDesignIds, designsMap]
  );
  const currentAppearance = useSelector(getCurrentAppearance);
  const printArea = useSelector(getPrintAreaForNewConfiguration);
  const context = useSelector(getContext);
  const features = useSelector(getFeatures);
  const productType = useSelector(getProductType);

  const [measureRef, dimension] = useDimension();
  const [scrollbarWidth, scrollContainerRef] = useScrollbarWidth([dimension, designs.length]);

  const gridWidth = dimension.width - scrollbarWidth;
  const columnCount = Math.floor(gridWidth / itemWidth) || 1;
  const columnWidth = Math.floor(gridWidth / columnCount);
  const rowCount = Math.ceil(favoritedDesignIds.length / columnCount);

  const printabilityMap = usePrintabilityMap(printArea, currentAppearance, designs);

  useEffect(() => {
    if (favoritedDesignIds.length !== designs.length) {
      const currentUnfetchedDesignIds = favoritedDesignIds.filter(
        (designId) =>
          !designs.find((design) => design.id === designId) &&
          !unfetchedDesignIds.current.includes(designId)
      );

      if (currentUnfetchedDesignIds.length) {
        currentUnfetchedDesignIds.forEach((designId) =>
          dispatch(fetchDesignAction(designId)).then(
            (design) => {
              if (designId !== design.id) {
                dispatch(updateDesignFavoriteAction(designId, design.id));
              }
            },
            // remove the favorite if we cant load the design
            () => dispatch(toggleDesignFavoriteAction(designId))
          )
        );
        unfetchedDesignIds.current = unfetchedDesignIds.current.concat(currentUnfetchedDesignIds);
      }
    }
  }, [dispatch, designs, favoritedDesignIds]);

  const itemData: CellData = {
    columnCount,
    designs,
    printabilityMap,
    imageHeight,
    favoritedDesignIds,
    showDesignPrices: features.showDesignPrices,
    context,
    productType,
    selectDesign,
    toggleDesignFavorite,
  };

  return (
    <div className="favorite-list" ref={measureRef}>
      <FixedSizeGrid
        outerRef={scrollContainerRef}
        className="favorite-list__grid"
        width={dimension.width}
        height={dimension.height}
        columnCount={columnCount}
        columnWidth={columnWidth}
        rowCount={rowCount}
        rowHeight={
          imageHeight +
          55 /* 8px (margin-top) + 16px (margin image) + 2px (border image) + 29px (height info) */
        }
        itemData={itemData}
      >
        {Cell}
      </FixedSizeGrid>
    </div>
  );
};
